import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Sparkles, Briefcase, Compass } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Input } from "@/components/kit";

const ROLES = ["Pemilik Bisnis / UMKM", "Marketing & Sales", "Agency / Jasa Digital", "Freelancer", "Lainnya"];
const SOURCES = ["Google Search", "Instagram", "TikTok", "Facebook", "YouTube", "Teman / Rekomendasi", "Lainnya"];

export default function OnboardingStepper() {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.name || "");
  const [role, setRole] = useState("");
  const [source, setSource] = useState("");
  const [saving, setSaving] = useState(false);

  const next = () => {
    if (step === 1 && name.trim().length < 2) { toast.error("Isi nama kamu dulu ya"); return; }
    if (step === 2 && !role) { toast.error("Pilih salah satu"); return; }
    setStep((s) => s + 1);
  };

  const finish = async () => {
    if (!source) { toast.error("Pilih salah satu"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/onboarding", { name: name.trim(), role, source });
      setUser(data);
      toast.success(`Selamat datang, ${data.name.split(" ")[0]}! 🎉`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="onboarding-stepper">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-float-up">
        <div className="h-1.5 bg-slate-100"><div className="h-full bg-sky-500 transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }} /></div>
        <div className="p-7 sm:p-9">
          <div className="flex items-center gap-2 text-sky-600 mb-1"><Sparkles className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wide">Langkah {step} dari 3</span></div>

          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Halo! Siapa nama kamu?</h2>
              <p className="text-sm text-slate-500 mt-1.5">Kami pakai ini untuk menyapa & personalisasi pengalamanmu.</p>
              <Input data-testid="onboarding-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Nama lengkap" className="mt-5 text-base"
                onKeyDown={(e) => e.key === "Enter" && next()} />
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <h2 className="font-display text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Briefcase className="w-6 h-6 text-sky-500" /> Anda siapa?</h2>
              <p className="text-sm text-slate-500 mt-1.5">Bantu kami memahami kebutuhanmu.</p>
              <div className="grid gap-2.5 mt-5">
                {ROLES.map((r) => (
                  <button key={r} onClick={() => setRole(r)} data-testid={`onboarding-role-${r}`}
                    className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-semibold transition ${role === r ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-100 text-slate-600 hover:border-slate-200"}`}>
                    {r} {role === r && <Check className="w-4 h-4 inline float-right text-sky-500" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <h2 className="font-display text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Compass className="w-6 h-6 text-sky-500" /> Tahu RESADA dari mana?</h2>
              <p className="text-sm text-slate-500 mt-1.5">Supaya kami bisa menjangkau lebih banyak orang sepertimu.</p>
              <div className="grid grid-cols-2 gap-2.5 mt-5">
                {SOURCES.map((s) => (
                  <button key={s} onClick={() => setSource(s)} data-testid={`onboarding-source-${s}`}
                    className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-semibold transition ${source === s ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-100 text-slate-600 hover:border-slate-200"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-7">
            {step > 1 ? (
              <button onClick={() => setStep((s) => s - 1)} className="text-sm text-slate-500 font-semibold inline-flex items-center gap-1 hover:text-slate-700"><ArrowLeft className="w-4 h-4" /> Kembali</button>
            ) : <span />}
            {step < 3 ? (
              <Button data-testid="onboarding-next" onClick={next} size="lg">Lanjut <ArrowRight className="w-4 h-4" /></Button>
            ) : (
              <Button data-testid="onboarding-finish" onClick={finish} loading={saving} size="lg">Mulai Pakai RESADA <Check className="w-4 h-4" /></Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
